import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { AnimatedValue } from '../../../../components/AnimatedValue';
import { NO_MIN_TARGET } from '../../../../root/touchTheme';
import { DOT_BREATHE_SX } from '../../../../styles/realtimeAnimations';
import LayersIcon from '@mui/icons-material/Layers';
import LayersClearIcon from '@mui/icons-material/LayersClear';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { formatValue, panelTitle } from './format';
import { isLiveReadout, readout } from './panelData';
import { seriesToggle } from './placement';
import { SeriesSwatch } from './SeriesSwatch';
import {
    isOwnPanel,
    type ChartSeries,
    type PanelId,
    type PanelPlan,
} from './seriesTypes';
import { useCompactChart } from './useCompactChart';
import { hasChange } from './valuesStrip';
import { useViewport } from './viewport';

export const PANEL_HEADER_HEIGHT = 24;
export const AWAY_LEGEND_HEIGHT = 20;

const VALUE_SX = {
    fontSize: '11px',
    fontFamily: 'monospace',
    fontWeight: 600,
} as const;
const LIVE_VALUE_SX = { ...VALUE_SX, borderRadius: '2px', px: '2px' } as const;
const ROW_SX = {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
} as const;
const ROW_GAP = 1.5;
// With a second legend row the first one has to hold the outcome names of a
// busy event at 800 px, so its gap is 4 px less.
const TIGHT_ROW_GAP = 1;
const LEGEND_ITEM_SX = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    minWidth: 0,
} as const;

// Below 900 px the legend of a busy panel (six clock lines) does not fit its
// row: the items keep their size and the row scrolls sideways, where they used
// to shrink under their own values and be drawn over each other. A finger
// scrolls it without a bar; a mouse has no such gesture, so a thin bar (4 px,
// inside the row's 24) says that the row scrolls. Chromium ignores
// ::-webkit-scrollbar once scrollbar-width is set, hence `auto` there and the
// thin width only where the pseudo-element does not exist (Firefox).
const SCROLL_ROW_SX = {
    overflow: undefined,
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    '@media (pointer: fine)': {
        scrollbarWidth: 'auto',
        '&::-webkit-scrollbar': { display: 'block', height: 4 },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: '2px',
            backgroundColor: (theme: Theme) =>
                alpha(theme.palette.text.primary, 0.3),
        },
    },
    '@supports not selector(::-webkit-scrollbar)': {
        '@media (pointer: fine)': { scrollbarWidth: 'thin' },
    },
} as const;
const LEGEND_ITEM_FIXED_SX = { ...LEGEND_ITEM_SX, flexShrink: 0 } as const;

interface PanelHeaderProps {
    plan: PanelPlan;
    readoutTime: number;
    marginLeft: number;
    marginRight: number;
}

// Series that are drawn here but live in another panel and have a short
// legend text of their own. They are listed in a second row, so they take no
// room from the legends of the panel's own series.
function awayLegendOf(plan: PanelPlan): ChartSeries[] {
    return plan.series.filter(
        s =>
            s.data.kind !== 'markers' &&
            s.awayLabel !== undefined &&
            s.home !== plan.id
    );
}

export function panelHeaderHeight(plan: PanelPlan): number {
    return awayLegendOf(plan).length > 0
        ? PANEL_HEADER_HEIGHT + AWAY_LEGEND_HEIGHT
        : PANEL_HEADER_HEIGHT;
}

export function toggleLabel(series: ChartSeries, target: PanelId): string {
    if (target === 'probability') {
        return series.home === 'probability'
            ? 'Back to the main chart'
            : 'Show on the main chart';
    }
    if (isOwnPanel(target)) return 'Show in its own panel';
    return `Back to the ${panelTitle(target, '')} panel`;
}

export function PanelHeader({
    plan,
    readoutTime,
    marginLeft,
    marginRight,
}: PanelHeaderProps) {
    const viewport = useViewport();
    // Below 900 px the values strip above the chart lists the main panel's
    // outcomes with their values: the legend would only draw them again, over
    // each other (seven outcomes in 300 px). The title, the live tag and the
    // row of lines from other panels stay, and so does every line the strip
    // does not list (flow, position) or that has a button to move it: its
    // legend item is the only place for its value and that button.
    const compact = useCompactChart();
    const stripCovers = (s: ChartSeries): boolean =>
        compact &&
        plan.id === 'probability' &&
        hasChange(s) &&
        !(
            viewport.moveSeries &&
            seriesToggle(s, viewport.allSeries, viewport.moves)
        );
    const title =
        viewport.panelTitles[plan.id] ??
        panelTitle(plan.id, plan.series[0]?.label ?? '');
    const canHide = viewport.hidePanel !== null && plan.id !== 'probability';
    const extra = viewport.panelExtras[plan.id];
    const hovering = viewport.cursor !== null;
    const liveIds = new Set(
        hovering
            ? []
            : plan.series
                  .filter(s => isLiveReadout(s, readoutTime))
                  .map(s => s.id)
    );
    const away = awayLegendOf(plan);
    const awayIds = new Set(away.map(s => s.id));
    const gap = away.length > 0 ? TIGHT_ROW_GAP : ROW_GAP;

    const swatch = (s: ChartSeries) => (
        <SeriesSwatch series={s} panelId={plan.id} />
    );

    // The button that moves one series to another panel, and back.
    const toggleOf = (s: ChartSeries) => {
        const toggle = viewport.moveSeries
            ? seriesToggle(s, viewport.allSeries, viewport.moves)
            : null;
        if (!toggle) return null;
        const toMain = toggle.target === 'probability';
        return (
            <Tooltip
                title={
                    toggle.possible
                        ? toggleLabel(s, toggle.target)
                        : 'The main chart has no free axis'
                }
            >
                <span>
                    <IconButton
                        size="small"
                        disabled={!toggle.possible}
                        aria-label={`${toggleLabel(s, toggle.target)}: ${s.label}`}
                        onClick={() =>
                            viewport.moveSeries?.(s.id, toggle.target)
                        }
                        sx={{ p: '1px', ...NO_MIN_TARGET }}
                    >
                        {toMain ? (
                            <LayersIcon sx={{ fontSize: 13 }} />
                        ) : (
                            <LayersClearIcon sx={{ fontSize: 13 }} />
                        )}
                    </IconButton>
                </span>
            </Tooltip>
        );
    };

    return (
        <Box
            data-testid={`chart-header-${plan.id}`}
            sx={{ height: panelHeaderHeight(plan) }}
        >
            <Box
                sx={{
                    ...ROW_SX,
                    // Below 900 px the legend of a busy panel (six clock lines)
                    // does not fit: its items keep their size and the row
                    // scrolls sideways, where they used to shrink under their
                    // own values and be drawn over each other.
                    ...(compact && SCROLL_ROW_SX),
                    gap,
                    height: PANEL_HEADER_HEIGHT,
                    pl: `${marginLeft}px`,
                    pr: `${marginRight}px`,
                }}
            >
                <Typography
                    sx={{
                        fontSize: '10px',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: 'text.disabled',
                        flexShrink: 0,
                    }}
                >
                    {title}
                </Typography>
                {canHide && (
                    <Tooltip title="Hide this panel">
                        <IconButton
                            size="small"
                            aria-label={`Hide the panel: ${title}`}
                            onClick={() => viewport.hidePanel?.(plan.id)}
                            sx={{
                                p: '1px',
                                ml: 0.5 - gap,
                                ...NO_MIN_TARGET,
                            }}
                        >
                            <VisibilityOffIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                    </Tooltip>
                )}
                {extra}
                {plan.series
                    .filter(
                        s =>
                            !stripCovers(s) &&
                            s.data.kind !== 'markers' &&
                            !awayIds.has(s.id)
                    )
                    .map(s => (
                        <Box
                            key={s.id}
                            data-legend-item
                            title={s.legendLabel ? s.label : undefined}
                            sx={compact ? LEGEND_ITEM_FIXED_SX : LEGEND_ITEM_SX}
                        >
                            {swatch(s)}
                            <Typography
                                sx={{
                                    fontSize: '11px',
                                    color: 'text.secondary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 180,
                                }}
                            >
                                {s.legendLabel ?? s.label}
                            </Typography>
                            {liveIds.has(s.id) ? (
                                <AnimatedValue
                                    value={readout(s, readoutTime) ?? 0}
                                    format={v => formatValue(s.unit, v)}
                                    sx={LIVE_VALUE_SX}
                                />
                            ) : (
                                <Typography
                                    sx={s.live ? LIVE_VALUE_SX : VALUE_SX}
                                >
                                    {formatValue(
                                        s.unit,
                                        readout(s, readoutTime)
                                    )}
                                </Typography>
                            )}
                            {toggleOf(s)}
                        </Box>
                    ))}
                {liveIds.size > 0 && (
                    <Tooltip title="Showing live prices">
                        <Box
                            data-testid="live-tag"
                            sx={{
                                ml: 'auto',
                                flexShrink: 0,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: 'success.main',
                                    ...DOT_BREATHE_SX,
                                }}
                            />
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    color: 'success.main',
                                }}
                            >
                                Live
                            </Typography>
                        </Box>
                    </Tooltip>
                )}
            </Box>
            {away.length > 0 && (
                <Box
                    data-testid="chart-away-legend"
                    sx={{
                        ...ROW_SX,
                        ...(compact && SCROLL_ROW_SX),
                        gap: ROW_GAP,
                        height: AWAY_LEGEND_HEIGHT,
                        pl: `${marginLeft}px`,
                        pr: `${marginRight}px`,
                    }}
                >
                    {away.map(s => (
                        <Box
                            key={s.id}
                            role="group"
                            aria-label={s.label}
                            title={s.label}
                            sx={compact ? LEGEND_ITEM_FIXED_SX : LEGEND_ITEM_SX}
                        >
                            {swatch(s)}
                            {/* The group has the full name; the short text is
                                only for the eye. */}
                            <Typography
                                aria-hidden="true"
                                sx={{
                                    fontSize: '11px',
                                    color: 'text.secondary',
                                }}
                            >
                                {s.awayLabel}
                            </Typography>
                            {toggleOf(s)}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}
